import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { emptyObject } from 'ui-components/default-props/empty';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { DomainStatus } from './domain-status';
import { messages } from './messages';
import { DomainRecordPropType } from './prop-types';
import { SenderEmailsTableEmptyRow } from './sender-emails-table-empty-row';
import style from './sender-emails-table.module.less';
import { createActionsColumnDefinition } from './table-cells/actions-cell';
import { createDomainStatusColumnDefinition } from './table-cells/domain-status-cell';
import { createEmailCellColumnDefinition } from './table-cells/email-address-cell';
import { createEmailVerificationStatusColumnDefinition } from './table-cells/email-verification-status-cell';
import { createStoreCellColumnDefinition } from './table-cells/store-cell';
import { VerifyDomainModal } from './verify-domain-modal/verify-domain-modal';
import { useModal } from '../../../hooks/use-modal';
import VerifyEmailModal from '../stores/selected-store/branding-tab/email-input/verify-email-modal';

const emailColumn = createEmailCellColumnDefinition();
const storeColumn = createStoreCellColumnDefinition();

const getBodyRowClassName = () => style['sender-emails-table-body-row'];

export const SenderEmailsTable = ({ domains, reloadDomains, isLoadingDomains, updateDomainData }) => {
  const [storeIdToAuthenticate, setStoreIdToAuthenticate] = useState('');
  const [emailToVerify, setEmailToVerify] = useState('');

  const { isOpen: isVerifyDomainModalOpen, open: openVerifyDomainModal, close: closeVerifyDomainModal } = useModal();

  const { isOpen: isVerifyEmailModalOpen, open: openVerifyEmailModal, close: closeVerifyEmailModal } = useModal();

  const onOpenVerifyDomainModal = useCallback(
    (storeId) => {
      setStoreIdToAuthenticate(storeId);
      openVerifyDomainModal();
    },
    [openVerifyDomainModal],
  );

  const onCloseVerifyDomainModal = useCallback(() => {
    closeVerifyDomainModal();
    setStoreIdToAuthenticate('');
  }, [closeVerifyDomainModal]);

  const onDomainAuthenticated = useCallback(
    (updatedDomain) => {
      updateDomainData(updatedDomain);

      if (updatedDomain.domainVerificationStatus === DomainStatus.Authenticated) {
        onCloseVerifyDomainModal();

        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.domainAuthenticationSuccess} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
    },
    [updateDomainData, onCloseVerifyDomainModal],
  );

  const onVerifyEmail = useCallback(
    (email) => {
      setEmailToVerify(email);
      openVerifyEmailModal();
    },
    [openVerifyEmailModal],
  );

  const onCloseVerifyEmailModal = useCallback(() => {
    closeVerifyEmailModal();
    setEmailToVerify('');
  }, [closeVerifyEmailModal]);

  const columns = useMemo(() => {
    return [
      emailColumn,
      storeColumn,
      createEmailVerificationStatusColumnDefinition(onVerifyEmail),
      createDomainStatusColumnDefinition(onOpenVerifyDomainModal),
      createActionsColumnDefinition(onVerifyEmail, onOpenVerifyDomainModal),
    ];
  }, [onVerifyEmail, onOpenVerifyDomainModal]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: domains,
    },
    useFlexLayout,
  );

  const domainToAuthenticate = useMemo(() => {
    return domains.find(({ storeId }) => storeId === storeIdToAuthenticate) || emptyObject;
  }, [storeIdToAuthenticate, domains]);

  return (
    <>
      <div className={style['table-overflow-container']}>
        <div className={style['table-min-width-container']}>
          <ReactTableWrapper
            className={style['sender-emails-table']}
            emptyRow={<SenderEmailsTableEmptyRow />}
            getBodyRowClassName={getBodyRowClassName}
            getTableBodyProps={getTableBodyProps}
            getTableProps={getTableProps}
            headerGroups={headerGroups}
            headerRowClassName={style['sender-emails-table-header-row']}
            isLoading={isLoadingDomains}
            prepareRow={prepareRow}
            rows={rows}
          />
        </div>
      </div>
      {isVerifyDomainModalOpen ? (
        <VerifyDomainModal
          domain={domainToAuthenticate}
          onClose={onCloseVerifyDomainModal}
          onDomainAuthenticated={onDomainAuthenticated}
          onDomainCreated={updateDomainData}
          onDomainUpdated={updateDomainData}
        />
      ) : null}
      {isVerifyEmailModalOpen ? (
        <VerifyEmailModal email={emailToVerify} getEmailVerificationStatus={reloadDomains} onClose={onCloseVerifyEmailModal} />
      ) : null}
    </>
  );
};

SenderEmailsTable.propTypes = {
  domains: PropTypes.arrayOf(DomainRecordPropType).isRequired,
  reloadDomains: PropTypes.func.isRequired,
  isLoadingDomains: PropTypes.bool.isRequired,
  updateDomainData: PropTypes.func.isRequired,
};
